import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, CircularProgress } from '@mui/material';
import { updateCallNotes } from '../../services/callsApi';

const AUTOSAVE_DELAY_MS = 900;

/**
 * Therapist-only in-call notes. Debounced autosave to
 * PATCH /api/calls/:id/notes.
 */
export default function CallNotesPane({ token, liveCallId, initialValue = '' }) {
  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimer = useRef(null);
  const lastSaved = useRef(initialValue);

  const flush = useCallback(async (text) => {
    if (!token || !liveCallId) return;
    if (text === lastSaved.current) return;
    setStatus('saving');
    try {
      await updateCallNotes({ token, liveCallId, notes: text });
      lastSaved.current = text;
      setStatus('saved');
    } catch (err) {
      console.error('[CallNotesPane] save failed:', err?.response?.data?.msg || err.message);
      setStatus('error');
    }
  }, [token, liveCallId]);

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setStatus('idle');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flush(next), AUTOSAVE_DELAY_MS);
  };

  const handleBlur = () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    flush(value);
  };

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  return (
    <Paper elevation={1} sx={{ display: 'flex', flexDirection: 'column', p: 2, height: '100%', minHeight: 240 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Session notes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {status === 'saving' && <CircularProgress size={12} />}
          <Typography variant="caption" color={status === 'error' ? 'error' : 'text.secondary'}>
            {status === 'saving' ? 'Saving…'
              : status === 'saved' ? 'Saved'
              : status === 'error' ? 'Save failed'
              : ''}
          </Typography>
        </Box>
      </Box>
      <TextField
        multiline
        minRows={8}
        maxRows={16}
        fullWidth
        placeholder="Type your notes. Autosaves as you go."
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        inputProps={{ maxLength: 20000 }}
        sx={{ flex: 1 }}
      />
    </Paper>
  );
}
